import React, { useState } from 'react';
import { Navigate, Link, useNavigate } from 'react-router-dom';
import { doSignInWithEmailAndPassword, doPasswordReset } from '../../Firebase/auth';
import { auth } from '../../Firebase/Firebase';
import { useAuth } from '../../Contexts/AuthContext/AuthContext';
import './Login.css'; 
import { verifyUserRoleByEmail } from '../../services/api';

const Login = () => {
  const { userLoggedIn, currentUser, userRole } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');
    
    try {
      // Sign in with Firebase
      await doSignInWithEmailAndPassword(email, password);
      
      // Get the current user from Firebase auth
      const user = auth.currentUser;
      if (!user || !user.email) {
        throw new Error('User not found or no email after sign in');
      }
      
      // After successful login, verify role with backend USING EMAIL
      try {
        const response = await verifyUserRoleByEmail(user.email);
        
        // Check user's role and redirect accordingly
        if (response.is_admin) {
          navigate('/admin');
        } else if (response.role === 'volunteer') {
          navigate('/volunteer');
        } else {
          setErrorMessage('Unable to determine your role. Please contact support.');
          await auth.signOut();
        }
      } catch (roleError) {
        console.error('Role verification error:', roleError);
        setErrorMessage('Role verification failed. Please try again.');
        await auth.signOut();
      }
    } catch (error) {
      // Handle Firebase authentication errors
      if (error.code === 'auth/user-not-found') {
        setErrorMessage('No account found with this email. Please sign up first.');
      } else if (error.code === 'auth/wrong-password') {
        setErrorMessage('Incorrect password. Please try again.');
      } else if (error.code === 'auth/invalid-email') {
        setErrorMessage('Invalid email address format.');
      } else {
        setErrorMessage(error.message || 'Failed to sign in. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsResettingPassword(true);
    setErrorMessage('');
    setSuccessMessage('');

    if (!email) {
      setErrorMessage('Please enter your email address');
      setIsResettingPassword(false);
      return;
    }

    try {
      await doPasswordReset(email);
      setSuccessMessage('Password reset email sent! Please check your inbox.');
      setShowForgotPassword(false);
    } catch (error) {
      if (error.code === 'auth/user-not-found') {
        setErrorMessage('No account found with this email address.');
      } else if (error.code === 'auth/invalid-email') {
        setErrorMessage('Invalid email address format.');
      } else {
        setErrorMessage(error.message || 'Failed to send password reset email. Please try again.');
      }
    } finally {
      setIsResettingPassword(false);
    }
  };

  if (userLoggedIn) {
    // Redirect based on actual user role from context
    if (userRole === 'admin') {
      return <Navigate to="/admin" />;
    } else if (userRole === 'volunteer') {
      return <Navigate to="/volunteer" />;
    }
    // If role not determined yet, stay on login page
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-card">
          <img src="/skillsbridge2.jpg" alt="SkillsBridge Logo" className="login-logo" />
          <h2>Login to SkillsBridge</h2>
          {errorMessage && <p className="error-message">{errorMessage}</p>}
          {successMessage && <p className="success-message" style={{ color: '#22c55e', marginBottom: '1rem' }}>{successMessage}</p>}
          
          {!showForgotPassword ? (
            <form className="login-form" onSubmit={onSubmit}>
            <div>
              <label>Email:</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>
            <div>
              <label>Password:</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>
              <button 
                type="submit" 
                className="login-button"
                disabled={isLoading}
              >
                {isLoading ? 'Logging in...' : 'Login'}
              </button>
            </form>
          ) : (
            <form className="login-form" onSubmit={handleForgotPassword}>
              <div>
                <label>Email:</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isResettingPassword}
                  placeholder="Enter your email"
                />
              </div>
              <button 
                type="submit" 
                className="login-button"
                disabled={isResettingPassword}
              >
                {isResettingPassword ? 'Sending...' : 'Send Reset Link'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForgotPassword(false);
                  setErrorMessage('');
                  setSuccessMessage('');
                }}
                style={{
                  marginTop: '10px',
                  background: 'transparent',
                  border: 'none',
                  color: '#12bbd1',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                Back to Login
              </button>
            </form>
          )}
          
          {!showForgotPassword && (
            <p style={{ marginTop: '10px', textAlign: 'center' }}>
              <a 
                href="#" 
                onClick={(e) => {
                  e.preventDefault();
                  setShowForgotPassword(true);
                }}
                style={{ 
                  color: '#12bbd1', 
                  textDecoration: 'none',
                  fontSize: '14px'
                }}
              >
                Forgot your password?
              </a>
            </p>
          )}
          
          <p className="signup-link">
            Don't have an account? <Link to="/application">Apply here</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;